import * as React from "react"
import { useIntl } from "react-intl"
import { ExclamationCircleFill } from "@walmart-web/livingdesign-icons"
import { Card, CardItem, TextField } from "@walmart-web/livingdesign-components"

import Button from "components/Shared/Button"
import { validateCriteria } from "helpers/password"
import LoginIcon from "assets/images/login_icon.svg"
import { resetPw } from "./service"
import PasswordCriteria from "./PasswordCriteria"
import TrailingShowPassword from "./TrailingShowPassword"

import "./ResetPasswordForm.scss"

interface CustomElements extends HTMLFormControlsCollection {
	code: HTMLInputElement
}

interface CustomForm extends HTMLFormElement {
	readonly elements: CustomElements
}

export type credentials = {
	code: string
	confirmPassword: string
}

const initialValues: credentials = {
	code: "",
	confirmPassword: "",
}

interface ResetPasswordFormProps {
	setError: () => void
	setSuccess: () => void
	setErrorType: (type: string) => void
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
	setError,
	setSuccess,
	setErrorType,
}): JSX.Element => {
	const intl = useIntl()

	const className = "reset-password-form"
	const [showPassword, setShowPassword] = React.useState<boolean>(false)
	const [showConfirmPassword, setShowConfirmPassword] = React.useState<boolean>(
		false
	)
	const [isDisabledSubmit, setIsDisabledSubmit] = React.useState<boolean>(true)
	const [flagHandleSubmit, setFlagHandleSubmit] = React.useState<boolean>(false)
	const [hasErrorPassword, setHasErrorPassword] = React.useState<boolean>(false)
	const [hasErrorPasswordsEquals, setHasErrorPasswordsEquals] = React.useState<
		boolean
	>(false)

	const [credentials, setCredentials] = React.useState<credentials>(
		initialValues
	)
	const [criteria, setCriteria] = React.useState(validateCriteria(""))

	const urlSearchParams = new URLSearchParams(location.search)
	const resetToken = urlSearchParams.get("resetToken")

	React.useEffect(() => {
		setIsDisabledSubmit(!checkCriterias())
	}, [criteria])

	React.useEffect(() => {
		setCriteria(validateCriteria(credentials.code))
	}, [credentials.code])

	React.useEffect(() => {
		if (!checkErrorPassword() && credentials.code != "") {
			resetPw(credentials.code, resetToken)
				.then(() => {
					setSuccess()
				})
				.catch((response: Error) => {
					setError()
					setErrorType(response.message)
				})
		}
	}, [hasErrorPassword, hasErrorPasswordsEquals, flagHandleSubmit])

	const checkErrorPassword = () => hasErrorPassword || hasErrorPasswordsEquals

	const checkCriterias = () => {
		const criteriaReady = Object.entries(criteria).some((k) => k[1] === true)
		return criteriaReady
	}

	const checkAllCriterias = () => {
		const criteriaReady = Object.entries(criteria).every((k) => k[1] === true)
		return criteriaReady
	}
	const handlePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
		const code = event.target.value
		setCredentials({ ...credentials, code })
	}

	const handleConfirmPassword = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const confirmPassword = event.target.value
		setCredentials({ ...credentials, confirmPassword })
	}

	const toggleShowPassword = () => {
		setShowPassword((showPassword) => !showPassword)
	}

	const toggleShowPasswordConfirm = () => {
		setShowConfirmPassword((showConfirmPassword) => !showConfirmPassword)
	}

	const validatePasswordsEquals = () =>
		credentials.code === credentials.confirmPassword

	const handleOnSubmit = async (event: React.FormEvent<CustomForm>) => {
		event.preventDefault()

		setHasErrorPassword(!checkAllCriterias())
		setHasErrorPasswordsEquals(!validatePasswordsEquals())
		setFlagHandleSubmit(true)
	}

	const validateConfirmPassword = () =>
		hasErrorPasswordsEquals && flagHandleSubmit

	const classNameConfirmPassword = () =>
		checkAllCriterias() ? "label" : "label-disabled"

	const showError = (showMessage: boolean, text: string) => {
		return (
			showMessage && (
				<div>
					<ExclamationCircleFill className="error-icon" />
					<span className="error-text">
						{intl.formatMessage({
							id: text,
						})}
					</span>
				</div>
			)
		)
	}

	return (
		<div className={`${className}`}>
			<Card size="medium" UNSAFE_className={`${className}-card-reset-password`}>
				<img src={LoginIcon} aria-hidden alt={""} />
				<CardItem>
					<div className={"reset-password-content"}>
						<div className="title">
							<h2>
								{intl.formatMessage({
									id: "phronesis.reset-password.title",
								})}
							</h2>
						</div>
						<div className="form">
							<form className="form-fields" onSubmit={handleOnSubmit}>
								<label htmlFor="password" className={"label"}>
									{intl.formatMessage({
										id: "phronesis.reset-password.form.pw",
									})}
								</label>
								<TextField
									label={undefined}
									id="code"
									type={showPassword ? "text" : "password"}
									value={credentials.code}
									data-testid="input-code"
									trailing={
										<TrailingShowPassword
											a11yLabel="Show password"
											actionClick={toggleShowPassword}
											isVisible={showPassword}
										/>
									}
									onChange={handlePassword}
								/>
								{showError(
									hasErrorPassword,
									"phronesis.reset-password.form.error.password.politics"
								)}
								<PasswordCriteria criteriaErrors={criteria} />
								<label
									htmlFor="password"
									className={`${classNameConfirmPassword()}`}
								>
									{intl.formatMessage({
										id: "phronesis.reset-password.form.confirm-pw",
									})}
								</label>
								<TextField
									label={undefined}
									type={showConfirmPassword ? "text" : "password"}
									value={credentials.confirmPassword}
									data-testid="input-confirm-pw"
									trailing={
										<TrailingShowPassword
											a11yLabel="Show confirm password"
											actionClick={toggleShowPasswordConfirm}
											isVisible={showConfirmPassword}
											isDisabled={!checkAllCriterias()}
										/>
									}
									onChange={handleConfirmPassword}
									disabled={!checkAllCriterias()}
								/>
								{showError(
									validateConfirmPassword(),
									"phronesis.reset-password.form.error.passwords.equals"
								)}

								<Button
									isFullWidth
									variant="primary"
									size="large"
									type="submit"
									disabled={isDisabledSubmit}
									className="submit-button"
								>
									{intl.formatMessage({
										id: "phronesis.reset-password.form.submit.button",
									})}
								</Button>
							</form>
						</div>
					</div>
				</CardItem>
			</Card>
		</div>
	)
}

export default ResetPasswordForm
